// Bundle Size Optimizations
import React from 'react';

// OPTIMIZATION: Dynamic imports that are resolved at runtime only
export const lazyImports = {
  // These functions return promises that resolve to modules
  // They're only called when actually needed, not during build
  loadLucideIcon: (iconName) => {
    return () => import('lucide-react').then(module => module[iconName]);
  }
};

// OPTIMIZATION: Code splitting helpers
export const createAsyncComponent = (importFn, fallback = null) => {
  return React.lazy(() => 
    importFn().catch(error => {
      console.error('Failed to load component:', error);
      // Return a fallback component
      return { 
        default: () => fallback || (
          <div className="p-4 text-center text-red-600">
            Failed to load component. Please refresh the page.
          </div>
        )
      };
    })
  );
};

// OPTIMIZATION: Preload critical components
export const preloadCriticalComponents = () => {
  // Preload components that are likely to be used soon
  const criticalImports = [
    () => import('../pages/Dashboard.jsx'),
    () => import('../pages/store/CooperativeStore.jsx'),
    () => import('../components/admin/ProductAffiliateSettings.jsx')
  ];

  criticalImports.forEach((importFn, index) => {
    // Stagger preloads to avoid overwhelming the browser
    setTimeout(() => {
      importFn().then(() => {
      }).catch(error => {
        console.warn(`🔧 PRELOAD: Failed to preload component ${index + 1}:`, error);
      });
    }, index * 100);
  });
};

// OPTIMIZATION: Dynamic imports with fallbacks
export const dynamicImport = async (importFn, retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const module = await importFn();
      return module;
    } catch (error) {
      console.warn(`🔧 DYNAMIC IMPORT: Attempt ${attempt} failed:`, error);
      
      if (attempt === retries) {
        throw error;
      }
      
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
};

// OPTIMIZATION: Tree shaking helpers (runtime only)
export const importSpecific = {
  // These are utility functions for runtime imports
  createSafeImport: (packageName) => {
    return () => {
      try {
        return import(packageName);
      } catch (error) {
        console.warn(`🔧 SAFE IMPORT: ${packageName} not available`);
        return Promise.resolve({ default: null });
      }
    };
  }
};

// OPTIMIZATION: Resource hints for better loading
export const addResourceHints = () => {
  // Add DNS prefetch for external resources
  const dnsPrefetchDomains = [
    'firestore.googleapis.com',
    'storage.googleapis.com',
    'cdn.loom.com'
  ];

  dnsPrefetchDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });

  // Add preconnect for critical resources
  const preconnectDomains = [
    'https://firestore.googleapis.com',
    'https://storage.googleapis.com'
  ];

  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    document.head.appendChild(link);
  });

};

// OPTIMIZATION: Monitor bundle performance
export const monitorBundlePerformance = () => {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') {
    return;
  }

  // Monitor initial bundle load time
  window.addEventListener('load', () => {
    const loadTime = performance.now();
    
    // Monitor memory usage
    if (performance.memory) {
      const memoryMB = Math.round(performance.memory.usedJSHeapSize / 1024 / 1024);
    }
  });

  // Monitor route changes
  let routeChangeStart = performance.now();
  const originalPushState = history.pushState;
  
  history.pushState = function(...args) {
    routeChangeStart = performance.now();
    return originalPushState.apply(this, args);
  };

  // Monitor when new content is rendered after route change
  const observer = new MutationObserver(() => {
    const routeChangeTime = performance.now() - routeChangeStart;
    if (routeChangeTime > 100) { // Only log significant route changes
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

// Initialize optimizations
export const initializeBundleOptimizations = () => {
  addResourceHints();
  monitorBundlePerformance();
  
  // Preload critical components after a short delay
  setTimeout(preloadCriticalComponents, 2000);
  
};
